import { useState } from "react";
import { ICommonServiceProps } from "@api/types/globalService.types";
import useGlobalDebounceHandler from "./useGlobalDebounceHandler";
import { useQuery } from "@tanstack/react-query";
import { CACHE_KEYS } from "@api/constants/cacheKeys";
import URLS from "@api/constants/urls";
import { handleGlobalGetRequestQuery } from "@api/functions/getRequests";
import { urlUtility } from "@repo/utilities/url.utility.ts";

interface IPlansFilter extends ICommonServiceProps {
  showActiveOnly?: boolean;
  isFilter?: boolean;
  partnerId?: number | string;
}

export const useGetAllPlansServices = ({
  enableQuery = true,
  page = "1",
  per_page = "15",
  cacheKeys = [],
  filters = {},
  showActiveOnly = true,
  isFilter = true,
  partnerId = "",
}: IPlansFilter) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { debouncedQuery } = useGlobalDebounceHandler(searchQuery);
  const urlQueries = urlUtility.getUrlQueries(location.search);
  const currPartnerId = +urlQueries["partner-id"] || partnerId || "";
  const journeyTypeId = +urlQueries["journey-type-id"] || "";
  const type = urlQueries["type"] || "";
  // Query's
  const allPlansService = useQuery({
    queryKey: [
      ...CACHE_KEYS.PLANS,
      page,
      debouncedQuery,
      per_page,
      showActiveOnly,
      currPartnerId,
      journeyTypeId,
      type,
      JSON.stringify(filters),
      ...cacheKeys,
    ],
    queryFn: () => {
      const searchParams = {
        "filter[name]": debouncedQuery,
        "filter[partner_id]": partnerId,
        "filter[journey_type_id]": journeyTypeId,
        ...(isFilter && { "filter[type]": type }),
        include:
          "partner.accounts,policies_count,payment_modes,combosCount,products,policies,products_count,combos",
        ...(showActiveOnly && { "filter[is_active]": true }),
        page,
        per_page,
        ...filters,
      };
      return handleGlobalGetRequestQuery({
        url: URLS.PLANS,
        searchParams,
      });
    },
    enabled: enableQuery,
  });
  return {
    services: { allPlansService },
    states: { searchQuery, setSearchQuery },
  };
};

export const useGetSinglePlansServices = () => {
  const urlQueries = urlUtility.getUrlQueries(location.search);
  const planId =
    urlQueries["plan-id"] ||
    urlQueries["policy-plan-id"] ||
    urlQueries["package-plan-id"] ||
    "";
  const singlePlanService = useQuery({
    queryKey: [...CACHE_KEYS.SINGLE_PLAN, planId],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: `${URLS.PLANS}/${planId}`,
        searchParams: {
          include: "combos",
        },
      }),
    enabled: planId ? true : false,
  });

  return {
    services: { singlePlanService },
  };
};
